<template>
  <div
    ref="panelEl"
    data-map-inset="impacts"
    class="raw-glass fixed inset-[--raw-edge-space] z-10 flex max-h-[calc(100dvh-var(--raw-header-offset)-var(--raw-footer-offset))] flex-col-reverse gap-8 px-[--raw-panel-space] py-8 max-md:bottom-[--raw-footer-offset] max-md:top-auto md:bottom-auto md:top-[var(--raw-header-offset)] md:w-[var(--raw-tablet-panel-width)] md:flex-col md:pt-16 lg:w-[580px]"
    :class="[
      !isExpanded
        ? 'h-[var(--raw-collapsed-height)]'
        : expanded
          ? 'h-[calc(100dvh-var(--raw-header-offset)-var(--raw-footer-offset))] md:h-auto'
          : 'h-[calc(85dvh-var(--raw-header-offset)-var(--raw-footer-offset))] md:h-auto',
      overlaid && 'max-lg:pointer-events-none max-lg:opacity-0',
    ]"
    :style="panelStyle"
  >
    <div v-if="isExpanded" ref="handleEl" data-drag-handle class="md:hidden" />

    <div ref="headerEl">
      <slot name="header" />
    </div>

    <Transition name="panel-body">
      <div v-if="isExpanded" class="flex min-h-0 flex-1 flex-col">
        <LenisScroll v-if="scrollBody" ref="scrollRef" viewport-class="flex-1">
          <slot name="body" />
        </LenisScroll>
        <slot v-else name="body" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import { useElementSize } from '@vueuse/core'

import LenisScroll from '~/scopes/campaign/components/UI/LenisScroll.vue'
import { useIsDesktop } from '~/scopes/campaign/composables/useIsDesktop'
import { useIsPhone } from '~/scopes/campaign/composables/useIsPhone'
import { usePanelDrag } from '~/scopes/campaign/composables/usePanelDrag'
import { useRootPxVar } from '~/scopes/campaign/composables/useRootPxVar'
import { useSheetExpansion } from '~/scopes/campaign/composables/useSheetExpansion'

const props = withDefaults(
  defineProps<{
    isExpanded: boolean
    collapsedRadius?: string
    expandedRadius?: string
    /** When false, the body owns its own static header + scroll, so it is rendered as-is rather than wrapped in a LenisScroll. */
    scrollBody?: boolean
    /** Open at full height rather than the default (a detail on phone); still draggable back down. */
    fullHeight?: boolean
    /** A modal has taken the screen: below the desktop split (where there is room for only one panel column) this one swaps out — slide + fade — leaving the map the block it frees. */
    overlaid?: boolean
  }>(),
  {
    collapsedRadius: '1.5rem',
    expandedRadius: '1.5rem',
    scrollBody: true,
    fullHeight: false,
  },
)

// Dragging the handle down collapses the panel; the parent owns the route change
// (it maps `collapse` to navigateToHome), keeping open/close URL-driven.
const emit = defineEmits<{ collapse: [] }>()

const panelEl = ref<HTMLElement | null>(null)
const handleEl = ref<HTMLElement | null>(null)
const headerEl = ref<HTMLElement | null>(null)
// Drag is a phone-only enhancement (<768), where the panel is a bottom sheet — at
// 768 and up it is a left rail and the handle is `md:hidden`.
const isPhone = useIsPhone()
const dragEnabled = computed(() => props.isExpanded && isPhone.value)
// At lg the rail and the modal fit side by side; below it only one is on screen, so
// only tablet plays the swap below.
const isDesktop = useIsDesktop()

// The swap transform must be inline (it shares one transition with height/radius),
// so it can't be tier-gated in CSS — it reads isPhone/isDesktop, which are false
// until mount. panelStyle uses this to hold the transform unshifted until then.
const mounted = ref(false)

// Drive the collapsed height from the pill's real size instead of a hardcoded
// constant, so it tracks its content (longer translated labels, font swaps).
const { height: headerHeight } = useElementSize(
  headerEl,
  { width: 0, height: 0 },
  { box: 'border-box' },
)
// Collapsed height = pill + the panel's own vertical padding, read off the element
// so it can't drift from the `py-*` utilities. Collapsed state is mobile-only,
// where that padding is constant, so a single read on mount is enough.
const padY = ref(0)
onMounted(() => {
  mounted.value = true
  const el = panelEl.value
  if (!el) return
  const style = getComputedStyle(el)
  padY.value = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
})
// Null until measured (SSR / first paint); the `--raw-collapsed-height` :root
// fallback in layout.css covers that window.
const collapsedHeight = computed(() =>
  headerHeight.value > 0 ? headerHeight.value + padY.value : null,
)

// Published globally so the floating MapZoomControl (outside this subtree) can
// clear the collapsed pill; one StatePanel instance keeps the fixed name safe.
useRootPxVar(collapsedHeight, '--raw-collapsed-height')

// Two-level rest size: full when the body asks for it, else the default.
const { expanded } = useSheetExpansion(
  dragEnabled,
  computed(() => props.fullHeight),
)

const { height: dragHeight, dragging } = usePanelDrag(panelEl, handleEl, {
  enabled: dragEnabled,
  expanded,
  twoStage: true,
  onDismiss: () => emit('collapse'),
  // Let a downward drag shrink straight into the collapsed pill.
  minHeight: () => collapsedHeight.value ?? 0,
})

// Resting style + the transient drag height.
//
// The panel swaps with the ResponsiveModal that replaces it over one shared
// var(--raw-in-out-quart) beat — "you exit, I enter". Phone slides on Y (same spot),
// tablet on X (it withdraws left as the modal arrives right). At lg both fit, so the
// panel holds still — hence the `max-lg` opacity gate in the template.
//
// Until mounted the tier queries are unresolved (false on the server and the first
// client render, which must match it), so we hold the unshifted resting transform.
// Otherwise a desktop deep-link paints the SSR "tablet" translateX(-10%) at full
// opacity and — with nothing changing post-mount to re-patch it — stays cut off.
const panelStyle = computed(() => {
  const borderRadius = props.isExpanded
    ? props.expandedRadius
    : props.collapsedRadius
  const swap =
    'transform var(--raw-in-out-quart), opacity var(--raw-in-out-quart)'

  if (!mounted.value)
    return { borderRadius, transform: 'translateX(0)', transition: swap }

  // Phone bottom sheet: also eases its height + radius as it collapses/expands and
  // tracks the finger while dragging (height leaves the transition then).
  if (isPhone.value) {
    const style: Record<string, string | undefined> = {
      borderRadius,
      transform: props.overlaid ? 'translateY(10%)' : 'translateY(0)',
      transition: dragging.value
        ? `border-radius var(--raw-out-quart), ${swap}`
        : `height var(--raw-out-quart), border-radius var(--raw-out-quart), ${swap}`,
    }
    if (dragHeight.value !== null) style.height = `${dragHeight.value}px`
    return style
  }

  return {
    borderRadius,
    transform:
      !isDesktop.value && props.overlaid ? 'translateX(-10%)' : 'translateX(0)',
    transition: swap,
  }
})

const scrollRef = ref<InstanceType<typeof LenisScroll>>()

/** Forwarded so the owning panel can reset scroll position on content swap. */
function scrollToTop() {
  scrollRef.value?.scrollToTop()
}

defineExpose({ scrollToTop })
</script>

<style scoped>
/* Drag-handle styling lives in the shared assets/css/drag-handle.css. */
.panel-body-enter-active {
  transition:
    opacity 0.2s var(--raw-ease-out-quart) 0.05s,
    transform 0.25s var(--raw-ease-out-quart) 0.05s;
}
.panel-body-leave-active {
  transition:
    opacity 0.15s var(--raw-ease-in-quart),
    transform 0.15s var(--raw-ease-in-quart);
}
.panel-body-enter-from,
.panel-body-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
